// Loads catalog context + submits the Sales-rep Order Intake form through
// the unified manufacturer routing (dealer-create vs inventory host handoff).

import { useCallback, useEffect, useState } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { fetchOrgLocations, type OrgLocationRow } from "@/lib/locations/fetchOrgLocations";
import { resolveSellerLocationUuid } from "@/lib/locations/resolveSellerLocationUuid";
import {
  createOrderIntake,
  dealerCreateOrderIntake,
  legacyPosHandoffOrderIntake,
} from "@/lib/orderIntakeClient";
import { getLinkedOpportunityIdForLead } from "@/lib/opportunity/opportunityClient";
import { getOpsSettings } from "@/lib/opsPaperworkClientPartB";
import type { OrderManufacturerPartner } from "@/lib/sales-intake/manufacturerSubmissionRouting";
import { supabase } from "@/lib/supabase";

import {
  applyLeadIntakePrefill,
  clearLeadIntakePrefill,
  peekLeadIntakePrefill,
} from "@/lib/sales-intake/leadIntakePrefill";

import {
  buildSalesRepIntakeSubmit,
  newSalesRepIntakeDraft,
  salesRepIntakeSuccessMessage,
  type SalesRepIntakeDraft,
} from "./salesRepIntakeBuild";

export type ManufacturerOption = OrderManufacturerPartner;

function pickSellerLocationId(
  hint: string | null | undefined,
  orgLocations: OrgLocationRow[],
): string {
  return resolveSellerLocationUuid(hint, orgLocations) ?? orgLocations[0]?.id ?? "";
}

function parseManufacturerPartners(raw: unknown): OrderManufacturerPartner[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((x) => x && typeof x === "object")
    .map((x) => {
      const row = x as { id?: unknown; name?: unknown; on_platform?: unknown };
      const id = typeof row.id === "string" ? row.id.trim() : "";
      if (!id) return null;
      return {
        id,
        name: typeof row.name === "string" && row.name.trim() ? row.name.trim() : id,
        on_platform: Boolean(row.on_platform),
      };
    })
    .filter((x): x is OrderManufacturerPartner => x !== null);
}

export function useSalesRepIntakeForm() {
  const auth = useAuth();
  const orgId = auth.activeOrganizationId;

  const [prefillLeadId, setPrefillLeadId] = useState<string | null>(
    () => peekLeadIntakePrefill()?.lead_id ?? null,
  );
  const [prefillLinkedOpportunityId, setPrefillLinkedOpportunityId] = useState<string | null>(
    () => peekLeadIntakePrefill()?.linked_opportunity_id ?? null,
  );
  const [draft, setDraft] = useState<SalesRepIntakeDraft>(() => {
    const base = newSalesRepIntakeDraft({ sellerLocationId: "" });
    const prefill = peekLeadIntakePrefill();
    return prefill ? applyLeadIntakePrefill(base, prefill) : base;
  });
  const [manufacturers, setManufacturers] = useState<ManufacturerOption[]>([]);
  const [locations, setLocations] = useState<OrgLocationRow[]>([]);
  const [catalogBusy, setCatalogBusy] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [legacyPosHandoffUrl, setLegacyPosHandoffUrl] = useState<string | null>(null);
  const [submissionsRefreshKey, setSubmissionsRefreshKey] = useState(0);

  const hasManufacturerPartners = manufacturers.length > 0;
  const submittingToManufacturer = Boolean(draft.destinationManufacturerOrgId.trim());

  useEffect(() => {
    const prefill = peekLeadIntakePrefill();
    if (!prefill) return;
    setPrefillLeadId(prefill.lead_id);
    setPrefillLinkedOpportunityId(prefill.linked_opportunity_id ?? null);
    setDraft((prev) => applyLeadIntakePrefill(prev, prefill));
  }, []);

  useEffect(() => {
    if (!prefillLeadId || prefillLinkedOpportunityId) return;
    let cancelled = false;
    void getLinkedOpportunityIdForLead(prefillLeadId).then((r) => {
      if (cancelled || !r.ok || !r.data.opportunity_id) return;
      setPrefillLinkedOpportunityId(r.data.opportunity_id);
    });
    return () => {
      cancelled = true;
    };
  }, [prefillLeadId, prefillLinkedOpportunityId]);

  useEffect(() => {
    if (!orgId) return;
    setDraft((prev) => {
      if (prev.sellerLocationId && !prev.sellerLocationId.startsWith("lot_")) {
        return prev;
      }
      const resolved = pickSellerLocationId(auth.activeLocationId ?? prev.sellerLocationId, locations);
      if (!resolved || resolved === prev.sellerLocationId) return prev;
      return { ...prev, sellerLocationId: resolved };
    });
  }, [orgId, auth.activeLocationId, locations]);

  const loadCatalog = useCallback(async () => {
    if (!orgId || !supabase) {
      setCatalogError("Organization context is unavailable.");
      return;
    }
    setCatalogBusy(true);
    setCatalogError(null);
    const [settingsRes, locRes] = await Promise.all([
      getOpsSettings(),
      fetchOrgLocations(supabase, orgId),
    ]);
    if (!settingsRes.ok) {
      setCatalogBusy(false);
      setCatalogError(settingsRes.error);
      return;
    }
    const partners = parseManufacturerPartners(settingsRes.data.manufacturer_options);
    setManufacturers(partners);
    if (locRes.error) {
      setLocations([]);
      setCatalogError(locRes.error);
      setCatalogBusy(false);
      return;
    }
    setLocations(locRes.data);
    setCatalogBusy(false);
    setDraft((prev) => {
      const next = { ...prev };
      const resolvedSeller = pickSellerLocationId(
        prev.sellerLocationId || auth.activeLocationId,
        locRes.data,
      );
      if (resolvedSeller) next.sellerLocationId = resolvedSeller;
      if (!next.destinationManufacturerOrgId && partners.length === 1) {
        next.destinationManufacturerOrgId = partners[0]!.id;
      }
      return next;
    });
  }, [orgId, auth.activeLocationId]);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  const patchDraft = useCallback((patch: Partial<SalesRepIntakeDraft>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    setSubmitError(null);
    setSubmitSuccess(null);
    setLegacyPosHandoffUrl(null);
  }, []);

  const patchCustomer = useCallback((patch: Partial<SalesRepIntakeDraft["customer"]>) => {
    setDraft((prev) => ({ ...prev, customer: { ...prev.customer, ...patch } }));
    setSubmitError(null);
    setSubmitSuccess(null);
    setLegacyPosHandoffUrl(null);
  }, []);

  const dismissPrefill = useCallback(() => {
    clearLeadIntakePrefill();
    setPrefillLeadId(null);
    setPrefillLinkedOpportunityId(null);
    setDraft(newSalesRepIntakeDraft({ sellerLocationId: pickSellerLocationId(auth.activeLocationId, locations) }));
    setSubmitError(null);
    setSubmitSuccess(null);
    setLegacyPosHandoffUrl(null);
  }, [auth.activeLocationId, locations]);

  async function submit(): Promise<boolean> {
    setSubmitError(null);
    setSubmitSuccess(null);
    setLegacyPosHandoffUrl(null);
    const contactId = crypto.randomUUID();
    const provenance = {
      lead_id: prefillLeadId,
      linked_opportunity_id: prefillLinkedOpportunityId,
    };
    const built = buildSalesRepIntakeSubmit(draft, contactId, manufacturers, provenance);
    if (!built.ok) {
      setSubmitError(built.error);
      return false;
    }
    setSubmitBusy(true);
    const r =
      built.mode === "dealer_create"
        ? await dealerCreateOrderIntake(built.body)
        : built.mode === "legacy_pos_handoff"
          ? await legacyPosHandoffOrderIntake(built.body)
          : await createOrderIntake(built.body);
    setSubmitBusy(false);
    if (!r.ok) {
      setSubmitError(r.error);
      return false;
    }
    let success = salesRepIntakeSuccessMessage(built, manufacturers);
    if (built.mode === "own_org" && "intake" in r.data && r.data.intake?.id) {
      success = `Order submitted (${r.data.intake.id.slice(0, 8)}…) — now in your org's Pending Review queue.`;
    }
    if (built.mode === "dealer_create" && "intake" in r.data && r.data.intake?.id) {
      success = `Order submitted (${r.data.intake.id.slice(0, 8)}…) — ${success}`;
    }
    if (built.mode === "legacy_pos_handoff" && "legacy_pos_entry_url" in r.data) {
      setLegacyPosHandoffUrl(r.data.legacy_pos_entry_url);
      if (r.data.legacy_pos_contact_id) {
        success = `${success} A POS contact was pre-created — finish the order there.`;
      } else {
        success = `${success} Open POS to complete the manufacturer order entry.`;
      }
    }
    setSubmitSuccess(success);
    setSubmissionsRefreshKey((k) => k + 1);
    const reset = newSalesRepIntakeDraft({
      sellerLocationId: pickSellerLocationId(auth.activeLocationId ?? draft.sellerLocationId, locations),
      destinationManufacturerOrgId: draft.destinationManufacturerOrgId,
    });
    if (draft.destinationManufacturerOrgId && manufacturers.length === 1) {
      reset.destinationManufacturerOrgId = manufacturers[0]!.id;
    }
    setDraft(reset);
    clearLeadIntakePrefill();
    setPrefillLeadId(null);
    setPrefillLinkedOpportunityId(null);
    return true;
  }

  return {
    draft,
    prefillLeadId,
    prefillLinkedOpportunityId,
    dismissPrefill,
    patchDraft,
    patchCustomer,
    manufacturers,
    hasManufacturerPartners,
    submittingToManufacturer,
    locations,
    catalogBusy,
    catalogError,
    submitBusy,
    submitError,
    submitSuccess,
    legacyPosHandoffUrl,
    submissionsRefreshKey,
    submit,
    reloadCatalog: loadCatalog,
  };
}

